import {
  Box,
  Container,
  Heading,
  Text,
  Badge,
  VStack,
  type BoxProps,
} from "@chakra-ui/react";
import type { ReactNode } from "react";

export const SECTION_PY = { base: 16, md: 20, lg: 24 } as const;

type SectionShellProps = BoxProps & {
  id?: string;
  children: ReactNode;
  alt?: boolean;
};

export function SectionShell({ id, children, alt, ...rest }: SectionShellProps) {
  return (
    <Box
      as="section"
      id={id}
      py={SECTION_PY}
      bg={alt ? "barber.900" : "barber.400"}
      position="relative"
      overflow="hidden"
      {...rest}
    >
      {children}
    </Box>
  );
}

type SectionHeadingProps = {
  eyebrow?: string;
  title: string;
  description?: string;
  align?: "center" | "left";
};

export function SectionHeading({
  eyebrow,
  title,
  description,
  align = "center",
}: SectionHeadingProps) {
  return (
    <VStack
      spacing={4}
      textAlign={align}
      align={align === "center" ? "center" : "flex-start"}
      maxW={align === "center" ? "3xl" : "2xl"}
      mx={align === "center" ? "auto" : undefined}
      mb={{ base: 10, md: 12 }}
    >
      {eyebrow ? (
        <Badge
          px={3}
          py={1}
          borderRadius="full"
          bg="blackAlpha.400"
          color="orange.900"
          fontSize="xs"
          letterSpacing="wider"
          textTransform="uppercase"
        >
          {eyebrow}
        </Badge>
      ) : null}
      <Heading
        as="h2"
        size={{ base: "xl", md: "2xl" }}
        color="white"
        lineHeight="shorter"
      >
        {title}
      </Heading>
      {description ? (
        <Text fontSize={{ base: "md", md: "lg" }} color="barber.100">
          {description}
        </Text>
      ) : null}
    </VStack>
  );
}

export function SectionContainer({ children }: { children: ReactNode }) {
  return <Container maxW="container.xl">{children}</Container>;
}

export function HomeCard({
  children,
  ...rest
}: BoxProps & { children: ReactNode }) {
  return (
    <Box
      bg="barber.900"
      border="1px solid"
      borderColor="whiteAlpha.150"
      borderRadius="2xl"
      p={{ base: 5, md: 6 }}
      boxShadow="0 8px 32px rgba(0, 0, 0, 0.18)"
      transition="transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease"
      _hover={{
        transform: "translateY(-2px)",
        borderColor: "whiteAlpha.250",
        boxShadow: "0 12px 40px rgba(0, 0, 0, 0.24)",
      }}
      {...rest}
    >
      {children}
    </Box>
  );
}
